import moment from 'moment';
import 'moment/locale/es';

/**
 * Returns the symbol appropriate for the currency code
 * e.g $ for USD
 * or '' for an unrecognised code.
 */
export function currencySignForCode(currencyCode) {
  if (currencyCode === null) return '';
  if (currencyCode.toUpperCase() === 'GBP') return '£';
  if (currencyCode.toUpperCase() === 'USD') return '$';
  if (currencyCode.toUpperCase() === 'EUR') return '€';
  return '';
}

/**
 * Gets the first day not covered by the date ranges of the passed in packages. Does not chack the active flag of the entered packages
 * @param {Array} packages The list of packages to check the collective expiry date of
 * @param {moment} dateFrom The date-time to check from (usually today, now
 * @returns {moment} the moment the packages will expire. Or null if they will last forever.
 */
export function getDateOfExpiry(packages, dateFrom) {
  // Breaks with a null date, assume they meant today
  if (!dateFrom) {
    // eslint-disable-next-line no-param-reassign
    dateFrom = moment.utc();
  }

  if (!packages || packages.length === 0) return dateFrom;

  // if one of the packages doesnt have an end date, and has already started, there is no expiry date.
  const packagesStartedAtDate = packages.filter(currPack => moment.utc(currPack.packageStartDate).isBefore(dateFrom));
  const infinitePackage = packagesStartedAtDate.find(currPack => !currPack.packageEndDate);
  if (infinitePackage) return null;

  // If none of the packages are active at the current date. Then it has expired by now.
  const packagesValidAtDate = packagesStartedAtDate.filter(currPack => moment.utc(currPack.packageEndDate).isAfter(dateFrom));
  if (!packagesValidAtDate || packagesValidAtDate.length === 0) return dateFrom;

  // Get the last date of the current set of packages.
  // Use Recursion to check that there are no active packages at that date.
  const latestPackage = packagesValidAtDate.find((pack, index, packs) => (
    !(packs.find(tempPack => moment.utc(pack.packageEndDate).isBefore(moment.utc(tempPack.packageEndDate))))
  ));
  // Add one day since the package effiect
  return getDateOfExpiry(packages, moment.utc(latestPackage.packageEndDate).add(1, 'days'));
}
/** How we display moments as dates in the platform. Can change TODO possibly in future, get from translations like reports*/
export const DATE_FORMAT         = 'dddd Do MMMM YYYY';
export const DATE_FORMAT_US         = 'dddd MMMM Do YYYY';
export const DATE_FORMAT_SHORTER = 'DD MMMM YYYY';
export const DATE_FORMAT_SHORTER_US = 'MMMM DD YYYY';
export const DATE_TIME_FORMAT_12 = 'ddd MMM DD YYYY hh:mm A';
export const DATE_TIME_FORMAT_24 = 'ddd DD MMM YYYY HH:mm';


/**
 * Converts a date in the current locale to a UTC date,
 * keeping the date itself the same, just changing the time to match the start of the utc day.
 * I.E even though (E.G 00:30 13 September 2019 (IN BST) = 23:30 12 September 2019 UTC - it will return 00:00 13 september 2019 UTC
 * This it to help with the fact that antd datepicker will return a moment of the current time in the current locale,
 * but we usually want dates as they pertain to UTC.
 */
export function convertToUTCDate(date) {
  if (!date) return date;
  return moment.utc(date.format(DATE_FORMAT), DATE_FORMAT).startOf('day');
}

/**
 * Adds 1 year and takes away 1 day
 */
export function getYearExpiry(date) {
  if (!date) return date;
  return moment(date).add(1, 'years').add(-1, 'days');
}

/**
 * Get display version of JS Date
 */
export function getDateDisplay(jsDate, userLocale) {
  const theMoment = moment(jsDate);
  if (userLocale === 'en-US') {
    theMoment.locale('en');
    return theMoment.format(DATE_FORMAT_US);
  } else {
    theMoment.locale(userLocale ? userLocale.substring(0, 2) : 'en');
    return theMoment.format(DATE_FORMAT);
  }
}

/**
 * Get display version of locally formatted String Date
 */
export function getLocalTimeDisplay(isoFormattedStringDate, userLocale) {
  const theMoment = moment.parseZone(moment(isoFormattedStringDate, moment.ISO_8601));
  if (userLocale === 'en-US') {
    theMoment.locale('en');
    return theMoment.format(DATE_TIME_FORMAT_12);   //12 hour time
  } else {
    theMoment.locale(userLocale ? userLocale.substring(0, 2) : 'en');
    return theMoment.format(DATE_TIME_FORMAT_24);   //24 hour time
  }
}

/**
 * Get date only from ISO formatted string (no time or timezone)
 */
export function getDateOnlyFromISO(isoFormattedStringDate) {
  const year = isoFormattedStringDate.substring(0, 4)*1;
  const month = (isoFormattedStringDate.substring(5, 7)*1) - 1;
  const day = isoFormattedStringDate.substring(8, 10)*1;
  return new Date(year, month, day);
}

/**
 * Get date range for last week
 */
export function getLastWeekDateRange(){
  const endDate = moment().endOf('d');
  const startDate = moment().subtract(7, 'days').startOf('d');
  return [startDate.valueOf(), endDate.valueOf()];
}

/**
 * Return date expression in the format of a number made up of YYYYMMDD
 */
export function getDateComparisonNumber(inputJSDate) {
  const day = ('' + (inputJSDate.getDate() + 100)).substring(1, 3);
  const month = ('' + (inputJSDate.getMonth() + 101)).substring(1, 3);
  const year = ("" + inputJSDate.getFullYear());
  return parseInt('' + year + month + day);
}

/**
 * Get date from comparison date expression (YYYYMMDD)
 */
export function getDateFromComparisonNumber(comparisonDateNumber) {
  const dateExpression = '' + comparisonDateNumber;
  const year = dateExpression.substring(0, 4)*1;
  const month = (dateExpression.substring(4, 6)*1) - 1;
  const day = dateExpression.substring(6)*1;
  return new Date(year, month, day);
}

/**
 * Adjust JS date by adding or subtracting (pass in negative day argument) days
 */
export function addDaysToJSDate(inputJSDate, days) {
  const momentDate = moment(inputJSDate);
  const adjDate = momentDate.add(days, 'days');
  return adjDate.toDate();
}

/**
 * Adjust JS date by adding or subtracting (pass in negative month argument) months
 */
export function addMonthsToJSDate(inputJSDate, months) {
  const momentDate = moment(inputJSDate);
  const adjDate = momentDate.add(months, 'months');
  return adjDate.toDate();
}

/**
 * Adjust JS date by adding or subtracting (pass in negative year argument) years
 */
export function addYearsToJSDate(inputJSDate, years) {
  const momentDate = moment(inputJSDate);
  const adjDate = momentDate.add(years, 'years');
  return adjDate.toDate();
}

/**
 * Is the time period option moving up (getting longer)
 */
export function isTimePeriodMovingUp(currentOption, newOption) {
  const currentDays = getOptionInDays(currentOption);
  const newDays = getOptionInDays(newOption);
  if (currentDays === 1 && newDays === 7) {
    return false;  //special case for today moving to 1 week (will have data)
  } else {
    return newDays > currentDays;
  }
}

/**
 * Get date range for time period option
 */
export function getDateRangeForDashboardWidgetOption(option) {
  const todayDate = new Date();
  let fromDate = new Date();
  if (option === 1 || option === 2 || option === 4 || option === 7) {
    // 1 day or 1 week(7 days) or 2 weeks or 4 weeks
    const days = getOptionInDays(option);
    fromDate = addDaysToJSDate(todayDate, (-days + 1)); // including today
  } else if (option === 12) {
    // 12 months (all)
    fromDate = addYearsToJSDate(todayDate, -1);
    fromDate = addDaysToJSDate(fromDate, 1);
  }
  return [getDateComparisonNumber(fromDate), getDateComparisonNumber(todayDate)];
}

function getOptionInDays(option) {
  let days = 1;
  if (option === 2) { days = 14; }
  if (option === 4) { days = 28; }
  if (option === 7) { days = 7; }
  if (option === 12) { days = 365; }  //only used for option comparison and not date calculations
  return days;
}

